import { BED_JOINT_GAP_MM, COORDINATE_QUANTUM_MM, EPSILON } from './constants.js';
import type { PlacedBrick } from './brick.js';
import { WallModel } from './wall-model.js';
import type { Mm } from './units.js';
import { normalisePerpendGap, quantise } from './units.js';
import { fail } from './errors.js';

/** A solved set of new X coordinates, in wall list order. */
export interface PerpendSolution {
  readonly perpendGapMm: Mm;
  readonly x: Float64Array;
}

type UnionStatus = 'merged' | 'consistent' | 'conflict';

/**
 * SPECIFICATION.md clause 7 — re-solve every brick's X coordinate so the bond
 * survives a change of perpend joint.
 *
 * Each brick is a node whose potential is its new X coordinate, plus one extra
 * AXIS node whose potential is the wall origin. Constraints assert
 * `potential[b] - potential[a] = d`; a weighted disjoint-set structure
 * accumulates them.
 *
 * Every constraint is expressed DIRECTLY in terms of the new gap, never as a
 * delta from the old one. That is what makes repeated small adjustments land
 * on exactly the same coordinates as one direct adjustment (clause 7.1).
 *
 * Pure: reads the model, mutates nothing. `applyPerpendGap` does the commit,
 * which is what lets this half run in a worker (design document B6).
 */
export function solvePerpendGap(model: WallModel, value: number | string): PerpendSolution | null {
  const newGap = normalisePerpendGap(value);
  const oldGap = model.perpendGapMm;
  if (Math.abs(newGap - oldGap) <= EPSILON) return null;

  const bricks = model.bricks;
  const count = bricks.length;
  if (count === 0) return { perpendGapMm: newGap, x: new Float64Array(0) };

  const axisNode = count;
  const parent = new Int32Array(count + 1);
  for (let i = 0; i <= count; i++) parent[i] = i;
  const rank = new Int32Array(count + 1);
  /** potential[node] - potential[parent[node]], in mm at the requested gap. */
  const weight = new Float64Array(count + 1);

  const indexById = new Map<number, number>();
  for (let i = 0; i < count; i++) indexById.set((bricks[i] as PlacedBrick).id, i);

  // Iterative find with path compression, so a long chain cannot blow the
  // stack on a large wall.
  const path: number[] = [];
  const find = (node: number): number => {
    path.length = 0;
    let current = node;
    while (parent[current] !== current) {
      path.push(current);
      current = parent[current] as number;
    }
    const root = current;
    for (let i = path.length - 1; i >= 0; i--) {
      const n = path[i] as number;
      const p = parent[n] as number;
      weight[n] = (weight[n] as number) + (weight[p] as number);
      parent[n] = root;
    }
    return root;
  };

  /** Constrain potential[right] - potential[left] to `difference`. */
  const union = (left: number, right: number, difference: number): UnionStatus => {
    const leftRoot = find(left);
    const leftWeight = weight[left] as number;
    const rightRoot = find(right);
    const rightWeight = weight[right] as number;
    if (leftRoot === rightRoot) {
      const actual = rightWeight - leftWeight;
      return Math.abs(actual - difference) <= EPSILON ? 'consistent' : 'conflict';
    }
    const rootDifference = difference + leftWeight - rightWeight;
    if ((rank[leftRoot] as number) < (rank[rightRoot] as number)) {
      parent[leftRoot] = rightRoot;
      weight[leftRoot] = -rootDifference;
    } else {
      parent[rightRoot] = leftRoot;
      weight[rightRoot] = rootDifference;
      if (rank[leftRoot] === rank[rightRoot]) rank[leftRoot] = (rank[leftRoot] as number) + 1;
    }
    return 'merged';
  };

  const exactAdjacency: { neighbour: number; difference: number }[][] = Array.from(
    { length: count },
    () => [],
  );
  const exactConstraints: { left: number; right: number; difference: number }[] = [];
  const addExactConstraint = (left: number, right: number, difference: number): void => {
    (exactAdjacency[left] as { neighbour: number; difference: number }[]).push({ neighbour: right, difference });
    (exactAdjacency[right] as { neighbour: number; difference: number }[]).push({ neighbour: left, difference: -difference });
    exactConstraints.push({ left, right, difference });
  };

  // (a) Perpend joints — hard. A closed end joint must stay closed.
  for (let leftIndex = 0; leftIndex < count; leftIndex++) {
    const left = bricks[leftIndex] as PlacedBrick;
    const targetX = left.right + oldGap;
    for (const right of model.bricksInRect(targetX, left.y, targetX, left.top)) {
      if (right.id === left.id || Math.abs(right.x - targetX) > EPSILON) continue;
      // Require strict vertical overlap, so bricks merely in line do not bind.
      if (Math.min(left.top, right.top) <= Math.max(left.y, right.y) + EPSILON) continue;
      const rightIndex = indexById.get(right.id) as number;
      const difference = left.width + newGap;
      if (union(leftIndex, rightIndex, difference) === 'conflict') {
        fail('This wall cannot preserve all existing perpend joints at that spacing.');
      }
      addExactConstraint(leftIndex, rightIndex, difference);
    }
  }

  // (b) Y-axis anchors. A left, centre or right edge on the axis stays there
  // while runs expand or contract away from it. A conflicting anchor is
  // silently dropped rather than raising.
  const axisTargets = new Map<number, number>();
  for (let index = 0; index < count; index++) {
    const brick = bricks[index] as PlacedBrick;
    let target: number | null = null;
    if (Math.abs(brick.x) <= EPSILON) target = 0;
    else if (Math.abs(brick.centreX) <= EPSILON) target = -brick.width / 2;
    else if (Math.abs(brick.right) <= EPSILON) target = -brick.width;
    if (target !== null && union(axisNode, index, target) !== 'conflict') {
      axisTargets.set(index, target);
    }
  }

  // (c) Bed alignments — soft. Conflicts are tolerated: the union simply does
  // not take, and the relation shapes the solution without vetoing it.
  const halfTolerance = COORDINATE_QUANTUM_MM / 2 + EPSILON;
  interface BedConstraint {
    priority: number;
    lower: number;
    upper: number;
    difference: number;
    exact: boolean;
  }
  const bedConstraints: BedConstraint[] = [];
  for (let lowerIndex = 0; lowerIndex < count; lowerIndex++) {
    const lower = bricks[lowerIndex] as PlacedBrick;
    const targetY = lower.top + BED_JOINT_GAP_MM;
    for (const upper of model.bricksInRect(lower.x, targetY, lower.right, targetY)) {
      if (upper.id === lower.id || Math.abs(upper.y - targetY) > EPSILON) continue;
      if (Math.min(lower.right, upper.right) <= Math.max(lower.x, upper.x) + EPSILON) continue;
      const upperIndex = indexById.get(upper.id) as number;
      // Half-bond relations (priority 0) outrank flush alignments, so a
      // running bond is recognised as a half bond rather than as an
      // accidental edge coincidence.
      const relations: readonly (readonly [number, number, number, boolean])[] = [
        [0, Math.abs(upper.x - (lower.centreX + oldGap / 2)), lower.width / 2 + newGap / 2, false],
        [0, Math.abs(upper.right - (lower.centreX - oldGap / 2)), lower.width / 2 - newGap / 2 - upper.width, false],
        [0, Math.abs(lower.x - (upper.centreX + oldGap / 2)), -upper.width / 2 - newGap / 2, false],
        [0, Math.abs(lower.right - (upper.centreX - oldGap / 2)), lower.width - upper.width / 2 + newGap / 2, false],
        [1, Math.abs(upper.x - lower.x), 0, true],
        [2, Math.abs(upper.right - lower.right), lower.width - upper.width, true],
        [3, Math.abs(upper.centreX - lower.centreX), lower.width / 2 - upper.width / 2, true],
      ];
      let best: readonly [number, number, number, boolean] | null = null;
      for (const relation of relations) {
        if (relation[1] > halfTolerance) continue;
        if (best === null || relation[0] < best[0] || (relation[0] === best[0] && relation[1] < best[1])) {
          best = relation;
        }
      }
      if (best !== null) {
        bedConstraints.push({
          priority: best[0],
          lower: lowerIndex,
          upper: upperIndex,
          difference: best[2],
          exact: best[3],
        });
      }
    }
  }

  bedConstraints.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    const al = (bricks[a.lower] as PlacedBrick).id;
    const bl = (bricks[b.lower] as PlacedBrick).id;
    if (al !== bl) return al - bl;
    return (bricks[a.upper] as PlacedBrick).id - (bricks[b.upper] as PlacedBrick).id;
  });
  for (const constraint of bedConstraints) {
    const status = union(constraint.lower, constraint.upper, constraint.difference);
    if (constraint.exact && status !== 'conflict') {
      const quantised = quantise(constraint.difference);
      if (Math.abs(quantised - constraint.difference) <= EPSILON) {
        addExactConstraint(constraint.lower, constraint.upper, quantised);
      }
    }
  }

  // Convert relative potentials to absolute requested coordinates. Components
  // that reach the axis are placed absolutely; every free component keeps its
  // first-laid (lowest-id) brick exactly where it was.
  const rootCoordinates = new Map<number, number>();
  rootCoordinates.set(find(axisNode), -(weight[axisNode] as number));
  const byAscendingId = Array.from({ length: count }, (_, i) => i).sort(
    (a, b) => (bricks[a] as PlacedBrick).id - (bricks[b] as PlacedBrick).id,
  );
  for (const index of byAscendingId) {
    const root = find(index);
    if (!rootCoordinates.has(root)) {
      rootCoordinates.set(root, (bricks[index] as PlacedBrick).x - (weight[index] as number));
    }
  }
  const requestedX = new Float64Array(count);
  for (let i = 0; i < count; i++) {
    requestedX[i] = (rootCoordinates.get(find(i)) as number) + (weight[i] as number);
  }

  // Quantise one representative per exact-lattice component, then propagate.
  // Because the propagated differences are themselves on-lattice, quantising
  // along the tree is a no-op after the first rounding, and every exact
  // relationship in the component survives verbatim (clause 7.5).
  const newX = new Float64Array(count);
  const assigned = new Uint8Array(count);
  const visited = new Uint8Array(count);
  for (let start = 0; start < count; start++) {
    if (visited[start]) continue;
    const component: number[] = [];
    const stack = [start];
    visited[start] = 1;
    while (stack.length > 0) {
      const current = stack.pop() as number;
      component.push(current);
      for (const { neighbour } of exactAdjacency[current] as { neighbour: number; difference: number }[]) {
        if (!visited[neighbour]) {
          visited[neighbour] = 1;
          stack.push(neighbour);
        }
      }
    }
    const axisMembers = component.filter((index) => axisTargets.has(index));
    const pool = axisMembers.length > 0 ? axisMembers : component;
    let representative = pool[0] as number;
    for (const index of pool) {
      if ((bricks[index] as PlacedBrick).id < (bricks[representative] as PlacedBrick).id) {
        representative = index;
      }
    }
    newX[representative] = quantise(requestedX[representative] as number);
    assigned[representative] = 1;
    const queue = [representative];
    for (let head = 0; head < queue.length; head++) {
      const current = queue[head] as number;
      for (const { neighbour, difference } of exactAdjacency[current] as { neighbour: number; difference: number }[]) {
        if (assigned[neighbour]) continue;
        newX[neighbour] = quantise((newX[current] as number) + difference);
        assigned[neighbour] = 1;
        queue.push(neighbour);
      }
    }
  }

  // Verify the quantised result honours everything that was promised.
  for (const { left, right, difference } of exactConstraints) {
    if (Math.abs(((newX[right] as number) - (newX[left] as number)) - difference) > EPSILON) {
      fail('This wall cannot preserve all coordinated alignments at that perpend spacing.');
    }
  }
  for (const [index, target] of axisTargets) {
    if (Math.abs((newX[index] as number) - target) > EPSILON) {
      fail('This wall cannot preserve all Y-axis anchors at that perpend spacing.');
    }
  }

  return { perpendGapMm: newGap, x: newX };
}

/**
 * Apply one wall-wide perpend joint adjustment. All-or-nothing: on any failure
 * the wall is left bit-for-bit unchanged (clause 7.1). Returns whether
 * anything changed.
 */
export function applyPerpendGap(model: WallModel, value: number | string): boolean {
  const solution = solvePerpendGap(model, value);
  if (solution === null) return false;
  return commitPerpendSolution(model, solution);
}

/**
 * Commit a solution produced by `solvePerpendGap` — possibly in a worker. The
 * candidate wall is built with FULL clearance validation before adoption, so a
 * solve that is arithmetically sound but geometrically impossible still leaves
 * the live wall untouched.
 */
export function commitPerpendSolution(model: WallModel, solution: PerpendSolution): boolean {
  const internals = model._internals();
  const bricks = model.bricks;
  if (bricks.length === 0) {
    internals.setPerpendGap(solution.perpendGapMm);
    return true;
  }
  if (solution.x.length !== bricks.length) {
    fail('This wall cannot be re-coordinated to that perpend spacing without creating a clash.');
  }

  const originalNextId = internals.nextId();
  const candidate = new WallModel([], solution.perpendGapMm);
  // The outline is drawn in absolute coordinates and does not move with the
  // joints, so it carries across unchanged.
  candidate.setSketch(model.sketch);
  try {
    for (let index = 0; index < bricks.length; index++) {
      const brick = bricks[index] as PlacedBrick;
      candidate.addBrick(brick.kind, solution.x[index] as number, brick.y, {
        rotation: brick.rotation,
        materialId: brick.materialId,
        brickId: brick.id,
        validate: true,
      });
    }
  } catch {
    fail('This wall cannot be re-coordinated to that perpend spacing without creating a clash.');
  }

  internals.adopt(candidate);
  internals.setNextId(Math.max(internals.nextId(), originalNextId));
  return true;
}
